import { ReactNode, useEffect, useMemo, useState } from 'react';
import '../styles/launch-countdown.css';

type Unit = { label: string; value: number };

type Props = {
  /** Anything `Date` can parse; the gated content unlocks once this passes. */
  launchDate: string;
  eyebrow?: string;
  title: string;
  description?: string;
  /** Prefix for this instance's data-testid, e.g. "s25f-launch". */
  testid: string;
  /** Rendered in place of the countdown once `launchDate` has passed. */
  children?: ReactNode;
};

function unitsUntil(remainingMs: number): Unit[] {
  const totalSeconds = Math.floor(Math.max(remainingMs, 0) / 1000);
  return [
    { label: 'Days', value: Math.floor(totalSeconds / 86400) },
    { label: 'Hours', value: Math.floor((totalSeconds % 86400) / 3600) },
    { label: 'Minutes', value: Math.floor((totalSeconds % 3600) / 60) },
    { label: 'Seconds', value: totalSeconds % 60 },
  ];
}

/**
 * A reusable pre-launch gate: ticks down to `launchDate` and swaps in
 * `children` the moment it passes, so callers never see a "not yet" branch of
 * their own to maintain — mount this around anything that shouldn't be usable
 * before a fixed date.
 */
export default function LaunchCountdown({
  launchDate,
  eyebrow,
  title,
  description,
  testid,
  children,
}: Props) {
  const target = useMemo(() => new Date(launchDate).getTime(), [launchDate]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (Date.now() >= target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (now >= target) {
    return <>{children}</>;
  }

  return (
    <div className="launch-countdown" data-testid={`${testid}-countdown`}>
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h2 className="launch-countdown-title">{title}</h2>
      {description && <p className="launch-countdown-description">{description}</p>}
      <div className="launch-countdown-units">
        {unitsUntil(target - now).map((unit) => (
          <div className="launch-countdown-unit" key={unit.label}>
            <span className="launch-countdown-value">
              {String(unit.value).padStart(2, '0')}
            </span>
            <span className="launch-countdown-label">{unit.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
